import { type CalendarDaysResponse, type CalendarTimelineDay, type CalendarTimelineResponse } from '@/api/schema/calendar'
import { dayjs } from '@/api/utils/dayjs'
import { CalendarCard } from '@/app/components/CalendarCard/CalendarCard'
import { EventsCard } from '@/app/components/EventsCard/EventsCard'
import { Row } from '@/app/components/Flex/Flex'
import { Layout } from '@/app/components/Layout/Layout'
import { Loader } from '@/app/components/Loader/Loader'
import { MenuFooter } from '@/app/components/MenuFooter/MenuFooter'
import { MoonState } from '@/app/components/MoonState/MoonState'
import { Motion } from '@/app/components/Motion/Motion'
import { useBack } from '@/app/hooks/useBack'
import { Timeline } from '@/app/pages/MoonPage/Timeline/Timeline'
import { api } from '@/app/utils/api'
import { useCalendarSSRData } from '@/app/utils/app-data'
import { getMoonEventLink } from '@/app/utils/moon'
import { isCalendarMonthAllowed } from '@/shared/calendar/date-range'
import type { FunctionalComponent } from 'preact'
import { useEffect, useRef, useState } from 'preact/hooks'
import { useLocation, useSearch } from 'wouter-preact'

export const MoonPage: FunctionalComponent = () => {
  const search = useSearch()
  const [pathname] = useLocation()
  const back = useBack('~/tools')
  const initialData = useCalendarSSRData(`${pathname}${search}`)
  const initialCalendar = initialData?.type === 'calendar' ? initialData : null
  const [resp, setResp] = useState<CalendarDaysResponse | null>(initialCalendar?.response ?? null)
  const [timeline, setTimeline] = useState<CalendarTimelineDay[] | null>(initialCalendar?.timeline ?? null)
  const skipInitialCalendarRequest = useRef(Boolean(initialCalendar))
  const skipInitialTimelineRequest = useRef(Boolean(initialCalendar))
  const [timelineLoading, setTimelineLoading] = useState(false)
  const timelineParams = getTimelineParams(search, resp?.currentDate)

  useEffect(() => {
    if (skipInitialCalendarRequest.current) {
      skipInitialCalendarRequest.current = false
      return
    }

    api.get<CalendarDaysResponse>('/calendar')
      .then(r => {
        setResp(r.data)
        setTimeline(r.data.timeline)
      })
  }, [])

  useEffect(() => {
    if (skipInitialTimelineRequest.current) {
      skipInitialTimelineRequest.current = false
      return
    }

    if (!timelineParams || !timelineParams.fromQuery) {
      if (resp) {
        setTimeline(resp.timeline)
      }
      return
    }

    let isDisposed = false
    setTimelineLoading(true)
    api.get<CalendarTimelineResponse>('/calendar/timeline', {
      params: {
        month: timelineParams.month,
        year: timelineParams.year,
      },
    })
      .then((r) => {
        if (!isDisposed) {
          setTimeline(r.data.timeline)
        }
      })
      .finally(() => {
        if (!isDisposed) {
          setTimelineLoading(false)
        }
      })

    return () => {
      isDisposed = true
    }
  }, [
    resp,
    timelineParams?.fromQuery,
    timelineParams?.month,
    timelineParams?.year,
  ])

  return (
    <Layout>
      <Layout.Content
        onBack={back}
      >
        {!resp
          ? (
            <Loader.Container>
              <Loader type='base' />
            </Loader.Container>
          )
          : (
            <Motion>
              <Layout.Header as='h1'>
                Лунный календарь
              </Layout.Header>

              <Row gap={15}>
                <CalendarCard
                  date={`Сейчас, ${dayjs(resp.currentMoon.at).format('D MMMM')}`}
                  title={resp.currentMoon.title}
                  subTitle={
                    <MoonState
                      at={resp.currentMoon.at}
                      phaseId={resp.currentMoon.phase.id}
                      phaseName={resp.currentMoon.phase.title}
                      sing={resp.currentMoon.sign}
                      location={resp.location}
                    />
                  }
                  text={resp.currentMoon.summary}
                  link={getMoonEventLink(`~/moon/day/${resp.currentMoon.lunarDay}`, resp.currentMoon.at)}
                />

                <EventsCard
                  events={resp.importantEvents}
                  from={resp.importantFrom}
                />

                {timeline && timelineParams && (
                  <Timeline
                    events={timeline}
                    month={timelineParams.month}
                    year={timelineParams.year}
                    currentDate={resp.currentDate}
                    timezone={resp.location.tz}
                    loading={timelineLoading}
                  />
                )}
              </Row>
            </Motion>
          )}

        <MenuFooter />
      </Layout.Content>
    </Layout>
  )
}

function getTimelineParams(search: string, currentDate?: string) {
  const params = new URLSearchParams(search)
  const month = Number(params.get('month'))
  const year = Number(params.get('year'))
  const hasValidParams = Number.isInteger(month)
    && month >= 1
    && month <= 12
    && Number.isInteger(year)
    && year >= 1900
    && year <= 2100
  const currentMonth = currentDate ? dayjs(currentDate) : dayjs()
  const targetMonth = currentMonth
    .year(year)
    .month(month - 1)
  const fromQuery = hasValidParams && isCalendarMonthAllowed(targetMonth, currentMonth)

  if (!fromQuery && !currentDate) {
    return null
  }

  return {
    fromQuery,
    month: fromQuery ? month : currentMonth.month() + 1,
    year: fromQuery ? year : currentMonth.year(),
  }
}
